import { ActualServer, API_BASE_URL, Budget } from '../types';
import { auth } from './firebase';

async function getIdToken(): Promise<string | null> {
  try {
    const user = auth.currentUser;
    if (!user) return null;
    return await user.getIdToken();
  } catch {
    return null;
  }
}

export type ConnectManagedResult =
  | { status: 'connected'; server: ActualServer }
  | { status: 'no-budgets'; server: ActualServer }
  | { status: 'needs-budget-password'; budgets: Budget[]; selectedBudgetId: string }
  | { status: 'error'; message: string };

export async function connectManagedServer(params: {
  serverUrl: string;
  serverName: string;
  password: string;
  budgetPassword?: string;
  budgetName?: string;
  ensureBudget?: boolean;
  validateBudgetAccess: (
    server: ActualServer,
    budgetId: string,
    budgetPassword?: string
  ) => Promise<{ success: boolean; needsPassword: boolean; error?: string }>;
}): Promise<ConnectManagedResult> {
  const {
    serverUrl,
    serverName,
    password,
    budgetPassword,
    budgetName,
    ensureBudget = true,
    validateBudgetAccess
  } = params;

  const token = await getIdToken();
  if (!token) {
    return { status: 'error', message: 'AUTH_REQUIRED' };
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/auth-and-get-budgets`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        serverURL: serverUrl,
        password,
        ensureBudget,
        ...(budgetName ? { budgetName } : {})
      })
    });
  } catch (networkError) {
    return {
      status: 'error',
      message: networkError instanceof Error ? networkError.message : 'NETWORK_ERROR'
    };
  }

  if (!response.ok) {
    let errorData: { error?: string } = {};
    try {
      errorData = await response.json();
    } catch {
      // ignore
    }
    return { status: 'error', message: errorData.error || 'AUTH_FAILED' };
  }

  const data = (await response.json()) as { budgets?: Budget[] };
  const budgets = data.budgets || [];

  const baseServer: ActualServer = {
    id: Date.now().toString(),
    name: serverName,
    url: serverUrl,
    password,
    cachedBudgets: budgets,
    budgetsCachedAt: Date.now(),
    hostedByNimah: true
  };

  if (budgets.length === 0) {
    return { status: 'no-budgets', server: baseServer };
  }

  const selectedBudget = budgets[0];
  const tempServer: ActualServer = {
    id: 'temp',
    name: serverName,
    url: serverUrl,
    password
  };

  const validation = await validateBudgetAccess(
    tempServer,
    selectedBudget.id,
    budgetPassword
  );

  if (!validation.success) {
    if (validation.needsPassword && !budgetPassword) {
      return {
        status: 'needs-budget-password',
        budgets,
        selectedBudgetId: selectedBudget.id
      };
    }
    return {
      status: 'error',
      message: validation.error || 'BUDGET_ACCESS_FAILED'
    };
  }

  const connectedServer: ActualServer = {
    ...baseServer,
    defaultBudgetId: selectedBudget.id,
    defaultBudgetName: selectedBudget.name,
    defaultBudgetPassword: budgetPassword,
    defaultBudgetCurrency: selectedBudget.currency || 'USD',
    ...(budgetPassword
      ? { budgetPasswords: { [selectedBudget.id]: budgetPassword } }
      : {})
  };

  return { status: 'connected', server: connectedServer };
}

export function serversAfterConnect(
  existingServers: ActualServer[],
  connectedServer: ActualServer
): ActualServer[] {
  const withoutSameUrl = existingServers.filter(
    s => s.url.replace(/\/$/, '') !== connectedServer.url.replace(/\/$/, '')
  );
  return [...withoutSameUrl, connectedServer];
}
